import EditorActions from 'components/Common/EditorActions'
import React, { useState } from "react"
import {
  Button,
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap"
import FileUpload from "components/Common/FileUpload"
import { logoFilename } from "components/Common/SponsorBand/sponsorFields"
import {
  EVENT_SPONSOR_LOGO_PATH,
  EVENT_SPONSOR_TIERS,
  sponsorFormErrors,
  sponsorFormValues,
} from "./sponsorPayload"

// Add or edit one sponsor of the event. The same two fields as a system partner carry the public
// behaviour and are required even though the API would accept a sponsor without the second: the
// logo, which is the plate, and the website, which is what a click on the plate opens.
//
// A modal, as the partner form is: it puts the editor where the click was, and it is how the rest
// of CP edits a record. No contract, no markets, no "show on the band" — an event sponsor is on the
// event's page for the life of the event, or removed.

const labelStyle = { fontSize: 12, fontWeight: 600 }
const hintStyle = { fontSize: 11 }

const SponsorForm = ({ sponsor, defaultTier, onSave, onCancel, saving }) => {
  const [form, setForm] = useState(() => sponsorFormValues(sponsor, defaultTier))
  const [initialForm] = useState(form)
  const dirty = JSON.stringify(form) !== JSON.stringify(initialForm)
  const [errors, setErrors] = useState([])

  // Nothing is marked until the first attempt to save — but once something is marked, the marks have
  // to keep up. Leaving "a logo is required" under a logo that has just been uploaded reads as a
  // form that has stopped listening.
  const setField = (field, value) =>
    setForm(current => {
      const next = { ...current, [field]: value }
      setErrors(marked => (marked.length ? sponsorFormErrors(next) : marked))
      return next
    })

  const invalid = field => errors.includes(field)
  const editing = Boolean(form.id)

  const submit = () => {
    const formErrors = sponsorFormErrors(form)
    setErrors(formErrors)
    if (!formErrors.length) onSave(form)
  }

  return (
    <Modal isOpen scrollable size="xl" toggle={onCancel}>
      <ModalHeader toggle={onCancel}>{editing ? `Edit ${sponsor.name}` : "Add sponsor"}</ModalHeader>

      <ModalBody>
        <Row>
          <Col md={4}>
            <Label style={labelStyle}>Logo</Label>
            <FileUpload
              url={EVENT_SPONSOR_LOGO_PATH}
              defaultPhoto={logoFilename(form.logoUrl)}
              onChange={filename => setField("logoFilename", filename)}
            />
            {invalid("logo") && <div className="text-danger mt-1">A logo is required.</div>}
            <p className="text-muted mt-2 mb-0" style={hintStyle}>
              Square PNG, transparent background, at least 300×300. Every plate renders at the same
              size, so a non-square logo gets letterboxed rather than cropped.
            </p>
          </Col>

          <Col md={8}>
            <div className="mb-3">
              <Label for="sponsor-name" style={labelStyle}>
                Name
              </Label>
              <Input
                id="sponsor-name"
                bsSize="sm"
                placeholder="Head"
                value={form.name}
                invalid={invalid("name")}
                onChange={event => setField("name", event.target.value)}
              />
              <span className="text-muted" style={hintStyle}>
                Used as the image alt text and the link&apos;s accessible name.
              </span>
            </div>

            <div className="mb-3">
              <Label for="sponsor-website" style={labelStyle}>
                Website
              </Label>
              <Input
                id="sponsor-website"
                bsSize="sm"
                placeholder="head.com"
                value={form.website}
                invalid={invalid("website")}
                onChange={event => setField("website", event.target.value)}
              />
              <span className="text-muted" style={hintStyle}>
                Clicking the logo opens this in a new tab.
              </span>
            </div>

            <Row>
              <Col md={6} className="mb-3">
                <Label for="sponsor-tier" style={labelStyle}>
                  Tier
                </Label>
                <Input
                  id="sponsor-tier"
                  type="select"
                  bsSize="sm"
                  value={form.category}
                  invalid={invalid("tier")}
                  onChange={event => setField("category", event.target.value)}
                >
                  {EVENT_SPONSOR_TIERS.map(({ key, label }) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </Input>
                <span className="text-muted" style={hintStyle}>
                  Every plate is the same size — rank reads from the tier heading and the order.
                </span>
              </Col>
            </Row>
          </Col>
        </Row>
      </ModalBody>

      <ModalFooter><EditorActions dirty={dirty} pending={saving} saveLabel="Save">
        <Button color="secondary" size="sm" outline disabled={saving} onClick={onCancel}>
          Cancel
        </Button>
        <Button
          color="primary"
          size="sm"
          className="d-inline-flex align-items-center"
          disabled={saving}
          onClick={submit}
        >
          Save
          {saving && <Spinner className="ml-2" size="sm" animation="grow" variant="dark" />}
        </Button>
      </EditorActions></ModalFooter>
    </Modal>
  )
}

export default SponsorForm
